import { ReactElement, useState, useEffect, SyntheticEvent } from 'react'
import { Table } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import {
  PageWrapper,
  SectionTitle,
  EditableDropdown,
  FlexWrapper,
  TableWrapper,
  TableHeader,
  Loader,
  TableFooter,
  Input
} from '../../../../components'
import strings from '../../../../locale/en'
import { FormBackWrapper, TableRow } from '../../User/subcomponents'
import UserAction from '../../User/UserAction'
import { tableHeader } from './consts'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  deleteSession,
  getSessionById,
  getSessionList,
  setFormValues
} from '../../../../redux/session/actions'
import { RootState } from '../../../../redux/store'
import { AddSessionForm, Session } from '../../../../redux/session/types'
import {
  formatDate,
  getBranchDropdown,
  getInstituteDropdown,
  getBatchDropdown
} from '../../../../helpers'
import {
  getBatches,
  getBranches,
  getInstitutes
} from '../../../../redux/coachingCenter/actions'
import { DatePickerWrapper, ResetButton } from '../subcomponents'
import { format, setHours, setMinutes } from 'date-fns'
import { DATE_FORMAT_YYYYMMDD } from '../../../../const/dateFormat'
import { useHistory } from 'react-router-dom'
import ROUTES from '../../../../const/routes'
import { ROLE } from '../../../../helpers/determineMenu'

const SessionList = (): ReactElement => {
  const {
    sessions,
    isLoading,
    institutes,
    branches,
    batches,
    role,
    userDetail
  } = useSelector(
    (state: RootState) => ({
      sessions: state.session.sessionList,
      isLoading: state.session.isLoading,
      institutes: state.coachingCenter.institutes,
      branches: state.coachingCenter.branches,
      batches: state.coachingCenter.batches,
      role: state.user.userInfo?.role,
      userDetail: state.user?.userInfo?.userDetail
    }),
    shallowEqual
  )

  const dispatch = useDispatch()
  const history = useHistory()
  const { sessionList, pageNumber = 1, totalPages = 1 } = sessions || {}
  const [selectedCoachingCenter, setSelectedCoachingCenter] = useState(
    userDetail?.coachingCenterId || ''
  )
  const [selectedBranch, setSelectedBranch] = useState('')
  const [selectedBatch, setSelectedBatch] = useState('')
  const [selectedSessionDate, setSelectedSessionDate] = useState<any>()
  const instituteList = institutes ? getInstituteDropdown(institutes) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []

  const {
    menu: {
      admin: {
        instituteName,
        selectBranch,
        selectBatch,
        sessionList: sessionTitle,
        selectDate,
        reset
      }
    }
  } = strings

  const fetchSession = (pageNumber?: number) => {
    dispatch(
      getSessionList({
        pageNo: pageNumber || 1,
        ...(selectedCoachingCenter && {
          coachingCentreId: selectedCoachingCenter
        }),
        ...(selectedBranch && { branchIds: [selectedBranch] }),
        ...(selectedBatch && { batchIds: [selectedBatch] }),
        ...(selectedSessionDate && {
          sessionDate: format(selectedSessionDate, DATE_FORMAT_YYYYMMDD)
        })
      })
    )
  }

  const handleBranchSelect = (coachingCenterId: string, branchId: string) => {
    setSelectedBranch(branchId)
    dispatch(
      getBatches({
        coachingCentreId: coachingCenterId,
        branchId: branchId
      })
    )
    dispatch(
      getSessionList({
        pageNo: 1,
        coachingCentreId: coachingCenterId,
        branchIds: [branchId]
      })
    )
  }

  const superAdminFilter = (
    <>
      <EditableDropdown
        width="30%"
        dropdownList={instituteList}
        handleSelect={({ id }) => {
          setSelectedCoachingCenter(id)
          setSelectedBranch('')
          setSelectedBatch('')
          dispatch(
            getBranches({
              coachingCentreId: id
            })
          )
          dispatch(getSessionList({ pageNo: 1, coachingCentreId: id }))
        }}
        title={`${instituteName} *`}
        placeholder={instituteName}
      />
      <EditableDropdown
        width="30%"
        dropdownList={branchList}
        handleSelect={({ id }) => {
          setSelectedBatch('')
          handleBranchSelect(selectedCoachingCenter, id)
        }}
        title={`${selectBranch} *`}
        placeholder={selectBranch}
      />
    </>
  )

  const instituteAdminFilter = (
    <EditableDropdown
      width="30%"
      dropdownList={branchList}
      handleSelect={({ id }) => {
        setSelectedBatch('')
        handleBranchSelect(selectedCoachingCenter, id)
      }}
      title={`${selectBranch} *`}
      placeholder={selectBranch}
    />
  )

  const getFormattedDate = (time: string) => {
    let [hours, minutes] = time.split(':')
    let formattedDate = setHours(new Date(), Number(hours))
    formattedDate = setMinutes(formattedDate, Number(minutes))
    return formattedDate
  }

  useEffect(() => {
    if (role === ROLE.BRANCH_ADMIN) {
      const branchId = userDetail?.branchIds.length
        ? userDetail?.branchIds[0]
        : ''
      const instituteId = userDetail?.coachingCenterId || ''
      handleBranchSelect(instituteId, branchId)
    } else if (role === ROLE.INSTITUTE_ADMIN) {
      dispatch(
        getBranches({
          coachingCentreId: selectedCoachingCenter
        })
      )
      dispatch(
        getSessionList({ pageNo: 1, coachingCentreId: selectedCoachingCenter })
      )
    } else {
      dispatch(getSessionList({ pageNo: 1 }))
      dispatch(getInstitutes())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PageWrapper id="container">
      <SectionTitle title={sessionTitle} hasBorder />
      <FlexWrapper justifyContent="space-between">
        {role === ROLE.ADMIN && superAdminFilter}
        {role === ROLE.INSTITUTE_ADMIN && instituteAdminFilter}
        <EditableDropdown
          width="30%"
          dropdownList={batchList}
          handleSelect={({ id }) => {
            setSelectedBatch(id)
            dispatch(
              getSessionList({
                pageNo: 1,
                coachingCentreId: selectedCoachingCenter,
                branchIds: [selectedBranch],
                batchIds: [id]
              })
            )
          }}
          title={`${selectBatch} *`}
          placeholder={selectBatch}
        />
        <DatePickerWrapper>
          <DatePicker
            selected={selectedSessionDate}
            onChange={(date: Date) => {
              setSelectedSessionDate(date)
              dispatch(
                getSessionList({
                  pageNo: 1,
                  ...(selectedCoachingCenter && {
                    coachingCentreId: selectedCoachingCenter
                  }),
                  ...(selectedBranch && { branchIds: [selectedBranch] }),
                  ...(selectedBatch && { batchIds: [selectedBatch] }),
                  sessionDate: format(date, DATE_FORMAT_YYYYMMDD)
                })
              )
            }}
            placeholderText={selectDate}
            customInput={
              <Input
                value={selectedSessionDate}
                inputType="text"
                placeholder={selectDate}
                suffix={['far', 'calendar']}
              />
            }
          />
          {selectedSessionDate && (
            <ResetButton
              variant="outline-secondary"
              size="sm"
              onClick={(e: SyntheticEvent) => {
                e.preventDefault()
                setSelectedSessionDate('')
                dispatch(
                  getSessionList({
                    pageNo: 1,
                    ...(selectedCoachingCenter && {
                      coachingCentreId: selectedCoachingCenter
                    }),
                    ...(selectedBranch && { branchIds: [selectedBranch] }),
                    ...(selectedBatch && { batchIds: [selectedBatch] })
                  })
                )
              }}
            >
              {reset}
            </ResetButton>
          )}
        </DatePickerWrapper>
      </FlexWrapper>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <FormBackWrapper>
            <TableWrapper>
              <Table size="sm" responsive="sm">
                <TableHeader>
                  <TableRow>
                    {tableHeader?.map((header, index) => (
                      <th key={`completed-session-header-${index}`}>{header}</th>
                    ))}
                  </TableRow>
                </TableHeader>
                <tbody>
                  {sessionList?.map(
                    (
                      {
                        id,
                        sessionName,
                        topicName,
                        batchList,
                        branchList,
                        coachingCentre,
                        userDetail,
                        sessionDate,
                        startTime,
                        endTime,
                        subject,
                        status,
                        course,
                        sessionType,
                        platform,
                        zoomUser,
                        webExUserId
                      }: Session,
                      index
                    ) => (
                      <TableRow key={`session-${index}`}>
                        <td>{sessionName}</td>
                        <td>{topicName}</td>
                        <td>{subject?.courseName}</td>
                        <td>{course?.courseName}</td>
                        <td>
                          {branchList?.reduce(
                            (result, branch, index) =>
                              index === 0
                                ? branch?.branchName
                                : `${result}, ${branch?.branchName}`,
                            ''
                          )}
                        </td>
                        <td>
                          {batchList?.reduce(
                            (result, batch, index) =>
                              index === 0
                                ? batch?.batchName
                                : `${result}, ${batch?.batchName}`,
                            ''
                          )}
                        </td>
                        <td>{coachingCentre?.coachingCentreName}</td>
                        <td>{status}</td>
                        <td>{userDetail?.firstName}</td>
                        <td>{formatDate(sessionDate)}</td>
                        <td>{startTime}</td>
                        <td>{endTime}</td>
                        <td>
                          <UserAction
                            handleUserDetail={() => {
                              dispatch(getSessionById(id))
                              history.push(ROUTES.VIEW_SESSION)
                            }}
                            handleNewUserSelect={() => {
                              const formValues: AddSessionForm = {
                                sessionName,
                                topicName,
                                id,
                                sessionType,
                                sessionDate,
                                startTime: getFormattedDate(startTime),
                                endTime: getFormattedDate(endTime),
                                subjectId: subject?.id,
                                teacherId: userDetail?.id,
                                coachingCentreId: coachingCentre?.id,
                                platform,
                                courseId: course?.id,
                                zoomUserId: zoomUser?.id,
                                webExUserId
                              }
                              dispatch(setFormValues(formValues))
                              history.push(ROUTES.EDIT_SESSION)
                            }}
                            handleDelete={() => dispatch(deleteSession(id))}
                          />
                        </td>
                      </TableRow>
                    )
                  )}
                </tbody>
              </Table>
              <TableFooter
                currentPage={pageNumber}
                totalPages={totalPages}
                handleNext={(pageNumber: number) => fetchSession(pageNumber)}
                handlePrevious={(pageNumber: number) => fetchSession(pageNumber)}
              />
            </TableWrapper>
          </FormBackWrapper>
        </div>
      )}
    </PageWrapper>
  )
}

export default SessionList
